import { useQuery, useMutation } from "@tanstack/react-query";
import axios from "axios";
import { API_BASE_RELATIVE_PATH, API_BASE_PATH } from "../Config/Config";
import { getUserDetails } from "../utils/userDetailsUtils";

const getDesignPrinciples = async (search) => {
  const user = getUserDetails();
  let url = `${API_BASE_PATH}${API_BASE_RELATIVE_PATH}?action=getDesignPrinciples&companyid=${user.companyid}`;
  if (search) url += `&search=${encodeURIComponent(search)}`;
  const response = await axios.get(url);
  return response.data;
};

export const useGetDesignPrinciplesQuery = (search, options = {}) => {
  return useQuery({
    queryKey: ["designPrinciples", search],
    queryFn: () => getDesignPrinciples(search),
    ...options,
  });
};

const addDesignPrinciple = async ({ principleDescription, principleName }) => {
  const user = getUserDetails();
  const url = `${API_BASE_PATH}${API_BASE_RELATIVE_PATH}?action=addDesignPrinciple&companyid=${user.companyid}&principleDescription=${encodeURIComponent(principleDescription)}&principleName=${encodeURIComponent(principleName)}`;
  const response = await axios.get(url);
  return response.data;
};

export const useAddDesignPrincipleMutation = (options = {}) => {
  return useMutation({
    mutationFn: addDesignPrinciple,
    ...options,
  });
};

const toggleDesignPrinciple = async ({ id, isActive }) => {
  const user = getUserDetails();
  const response = await axios.get(
    `${API_BASE_PATH}${API_BASE_RELATIVE_PATH}?action=toggleDesignPrinciple&companyid=${user.companyid}&id=${id}&isActive=${isActive}`,
  );
  return response.data;
};

export const useToggleDesignPrincipleMutation = (options = {}) => {
  return useMutation({
    mutationFn: toggleDesignPrinciple,
    ...options,
  });
};

const editDesignPrinciple = async ({ id, principleDescription, principleName }) => {
  const user = getUserDetails();
  const url = `${API_BASE_PATH}${API_BASE_RELATIVE_PATH}?action=editDesignPrinciple&companyid=${user.companyid}&principleId=${id}&principleDescription=${encodeURIComponent(principleDescription)}&principleName=${encodeURIComponent(principleName)}`;
  const response = await axios.get(url);
  return response.data;
};

export const useEditDesignPrincipleMutation = (options = {}) => {
  return useMutation({
    mutationFn: editDesignPrinciple,
    ...options,
  });
};

const getUIDesignIssues = async ({ testCaseResultsId, testStepResultId }) => {
  const user = getUserDetails();
  const response = await axios.get(
    `${API_BASE_PATH}${API_BASE_RELATIVE_PATH}?action=getUIDesignIssues&companyid=${user.companyid}&testCaseResultsId=${testCaseResultsId}&testStepResultId=${testStepResultId}`,
  );
  return response.data;
};

export const useGetUIDesignIssuesQuery = (testCaseResultsId, testStepResultId, options = {}) => {
  return useQuery({
    queryKey: ["uiDesignIssues", testCaseResultsId, testStepResultId],
    queryFn: () => getUIDesignIssues({ testCaseResultsId, testStepResultId }),
    enabled: !!testCaseResultsId && !!testStepResultId,
    ...options,
  });
};

const getSelectedPrinciples = async ({ testCaseId, principleLevel, testStepId }) => {
  const user = getUserDetails();
  let url = `${API_BASE_PATH}${API_BASE_RELATIVE_PATH}?action=getSelectedPrinciples&companyid=${user.companyid}&testCaseId=${testCaseId}`;
  if (principleLevel) url += `&principleLevel=${principleLevel}`;
  if (principleLevel === "Test_step" && testStepId) url += `&testStepId=${testStepId}`;
  const response = await axios.get(url);
  return response.data;
};

export const useGetSelectedPrinciplesForTestCaseQuery = (testCaseId, options = {}) => {
  return useQuery({
    queryKey: ["selectedPrinciples", testCaseId],
    queryFn: () => getSelectedPrinciples({ testCaseId, principleLevel: "Test_case" }),
    enabled: !!testCaseId,
    ...options,
  });
};

const saveTestCaseDesignPrinciples = async ({ testCaseId, principleIds }) => {
  const user = getUserDetails();
  const response = await axios.get(
    `${API_BASE_PATH}${API_BASE_RELATIVE_PATH}?action=saveDesignPrinciples&companyid=${user.companyid}&testCaseId=${testCaseId}&principleIds=${principleIds}&principleLevel=Test_case`,
  );
  return response.data;
};

export const useSaveTestCaseDesignPrinciplesMutation = (options = {}) => {
  return useMutation({
    mutationFn: saveTestCaseDesignPrinciples,
    ...options,
  });
};

const uploadBaseImage = async ({
  testCaseId,
  image,
  principleLevel,
  testStepId,
  sourceTestStepId,
}) => {
  const user = getUserDetails();
  const formData = new FormData();
  if (image) formData.append("image", image);
  let url = `${API_BASE_PATH}${API_BASE_RELATIVE_PATH}?action=uploadBaseImage&companyid=${user.companyid}&testCaseId=${testCaseId}`;
  if (principleLevel) url += `&principleLevel=${principleLevel}`;
  if (principleLevel === "Test_step" && testStepId) url += `&testStepId=${testStepId}`;
  if (sourceTestStepId) url += `&sourceTestStepId=${sourceTestStepId}`;
  const response = await axios.post(url, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

export const useUploadBaseImageMutation = (options = {}) => {
  return useMutation({
    mutationFn: uploadBaseImage,
    ...options,
  });
};

const removeBaseImage = async ({ testCaseId, principleLevel, testStepId }) => {
  const user = getUserDetails();
  let url = `${API_BASE_PATH}${API_BASE_RELATIVE_PATH}?action=removeBaseImage&companyid=${user.companyid}&testCaseId=${testCaseId}`;
  if (principleLevel) url += `&principleLevel=${principleLevel}`;
  if (principleLevel === "Test_step" && testStepId) url += `&testStepId=${testStepId}`;
  const response = await axios.get(url);
  return response.data;
};

export const useRemoveBaseImageMutation = (options = {}) => {
  return useMutation({
    mutationFn: removeBaseImage,
    ...options,
  });
};

const getBaseImage = async ({ testCaseId, principleLevel, testStepId }) => {
  const user = getUserDetails();
  let url = `${API_BASE_PATH}${API_BASE_RELATIVE_PATH}?action=getBaseImage&companyid=${user.companyid}&testCaseId=${testCaseId}`;
  if (principleLevel) url += `&principleLevel=${principleLevel}`;
  if (principleLevel === "Test_step" && testStepId) url += `&testStepId=${testStepId}`;
  const response = await axios.get(url);
  return response.data;
};

export const useGetBaseImageQuery = (testCaseId, options = {}) => {
  return useQuery({
    queryKey: ["baseImage", testCaseId],
    queryFn: () => getBaseImage({ testCaseId }),
    enabled: !!testCaseId,
    ...options,
  });
};

export const useGetStepBaseImageQuery = (testCaseId, testStepId, options = {}) => {
  return useQuery({
    queryKey: ["stepBaseImage", testCaseId, testStepId],
    queryFn: () => getBaseImage({ testCaseId, principleLevel: "Test_step", testStepId }),
    enabled: !!testCaseId && !!testStepId,
    ...options,
  });
};

export const useGetSelectedPrinciplesForTestStepQuery = (testCaseId, testStepId, options = {}) => {
  return useQuery({
    queryKey: ["selectedStepPrinciples", testCaseId, testStepId],
    queryFn: () => getSelectedPrinciples({ testCaseId, principleLevel: "Test_step", testStepId }),
    enabled: !!testCaseId && !!testStepId,
    ...options,
  });
};

const saveTestStepDesignPrinciples = async ({ testCaseId, testStepId, principleIds }) => {
  const user = getUserDetails();
  const response = await axios.get(
    `${API_BASE_PATH}${API_BASE_RELATIVE_PATH}?action=saveDesignPrinciples&companyid=${user.companyid}&testCaseId=${testCaseId}&principleIds=${principleIds}&principleLevel=Test_step&testStepId=${testStepId}`,
  );
  return response.data;
};

export const useSaveTestStepDesignPrinciplesMutation = (options = {}) => {
  return useMutation({
    mutationFn: saveTestStepDesignPrinciples,
    ...options,
  });
};

const useDesignPrinciples = (testCaseResultsId, testStepResultId, search) => {
  const principlesQuery = useGetDesignPrinciplesQuery(search);
  const addPrincipleMutation = useAddDesignPrincipleMutation();
  const togglePrincipleMutation = useToggleDesignPrincipleMutation();
  const editPrincipleMutation = useEditDesignPrincipleMutation();
  const uiDesignIssuesQuery = useGetUIDesignIssuesQuery(testCaseResultsId, testStepResultId);

  return {
    principlesQuery,
    addPrincipleMutation,
    togglePrincipleMutation,
    editPrincipleMutation,
    uiDesignIssuesQuery,
  };
};

export default useDesignPrinciples;
